import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { auth, firestoreDb } from '../api/client';
import { onSnapshot, doc } from 'firebase/firestore';
import {
  startTrial,
  SubscriptionInfo,
  upgradeNow,
} from '../api/services/billing';

interface SubscriptionContextValue {
  loading: boolean;
  info?: SubscriptionInfo;
  daysRemaining?: number; // trial days remaining
  startTrialIfEligible: () => Promise<void>;
  upgrade: () => Promise<void>;
  dismissBanner: () => void;
  bannerDismissed: boolean;
}

const SubscriptionContext = createContext<SubscriptionContextValue | undefined>(
  undefined,
);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [info, setInfo] = useState<SubscriptionInfo | undefined>();
  const [loading, setLoading] = useState(true);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Compute trial remaining days
  const daysRemaining =
    info?.status === 'trialing' && info.trial_end
      ? Math.max(
          0,
          Math.ceil((info.trial_end - Date.now()) / (24 * 60 * 60 * 1000)),
        )
      : undefined;

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setLoading(false);
      return;
    }
    const subDoc = doc(firestoreDb, `users/${user.uid}/billing/subscription`);
    const unsub = onSnapshot(subDoc, snap => {
      setInfo(
        snap.exists() ? (snap.data() as SubscriptionInfo) : { status: 'none' },
      );
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const startTrialIfEligible = useCallback(async () => {
    if (!auth.currentUser) return;
    if (!info || info.status === 'none' || info.status === 'trial_expired') {
      try {
        setLoading(true);
        const res = await startTrial();
        setInfo(res);
      } finally {
        setLoading(false);
      }
    }
  }, [info]);

  const upgrade = useCallback(async () => {
    await upgradeNow();
  }, []);

  const dismissBanner = () => setBannerDismissed(true);

  const value: SubscriptionContextValue = {
    loading,
    info,
    daysRemaining,
    startTrialIfEligible,
    upgrade,
    dismissBanner,
    bannerDismissed,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export function useSubscription(): SubscriptionContextValue {
  const ctx = useContext(SubscriptionContext);
  if (!ctx)
    throw new Error('useSubscription must be used within SubscriptionProvider');
  return ctx;
}
